import { html, TemplateResult } from 'lit';
import { ref } from 'lit/directives/ref.js';

/** Full-viewport modal (pair with `HTMLDialogElement.showModal()`). */
export const shellModalClass = 'shell-modal';

function shellModalCloseButton(onClose: () => void): TemplateResult {
  return html`
    <button
      type="button"
      class="md-btn-icon md-sys-ripple touch-manipulation shrink-0"
      aria-label="Cerrar"
      @click=${onClose}
    >
      <svg class="md-btn-icon__glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  `;
}

export function shellCrudFormModalSubtitle(editing: boolean): string {
  return editing
    ? 'Modifique los datos y guarde los cambios en este dispositivo.'
    : 'Complete los datos y guarde en este dispositivo.';
}

export function shellModalHeader(opts: { title: string; subtitle?: string; onClose: () => void }): TemplateResult {
  return html`
    <div class="shell-modal__header">
      <div class="shell-modal__header-text">
        <h2 class="shell-modal__title">${opts.title}</h2>
        ${opts.subtitle ? html`<p class="shell-modal__subtitle">${opts.subtitle}</p>` : null}
      </div>
      ${shellModalCloseButton(opts.onClose)}
    </div>
  `;
}

export function syncHtmlDialogOpen(dialog: HTMLDialogElement | null | undefined, open: boolean): void {
  if (!dialog) return;
  if (open) {
    if (dialog.open) return;
    try {
      dialog.showModal();
    } catch {
      try {
        dialog.show();
      } catch {
        /* Test envs may lack modal APIs. */
      }
    }
  } else if (dialog.open) {
    dialog.close();
  }
}

export function shellDialogOpenRef(open: boolean) {
  return ref((el: Element | undefined) => {
    syncHtmlDialogOpen(el instanceof HTMLDialogElement ? el : null, open);
  });
}

export function syncHtmlDialogs(
  root: ParentNode | null | undefined,
  pairs: ReadonlyArray<{ selector: string; open: boolean }>,
): void {
  if (!root) return;
  for (const { selector, open } of pairs) {
    if (open) syncHtmlDialogOpen(root.querySelector(selector) as HTMLDialogElement | null, true);
  }
  for (const { selector, open } of pairs) {
    if (!open) syncHtmlDialogOpen(root.querySelector(selector) as HTMLDialogElement | null, false);
  }
}

export function shellRecordDetailMediaExtra(opts: {
  firmaUrl: string | null;
  fotoUrl: string | null;
  firmaMediaId: string | null;
  fotoMediaId: string | null;
}): TemplateResult {
  return html`
    ${opts.firmaUrl
      ? html`
          <div>
            <p class="mb-2 text-sm font-medium">Firma de quien recibe</p>
            <img
              src=${opts.firmaUrl}
              alt="Firma"
              class="block max-h-[8.75rem] w-full rounded-[var(--md-shape-corner-medium)] border border-dashed border-[color:var(--md-sys-color-outline-variant)] bg-white object-contain"
            />
          </div>
        `
      : opts.firmaMediaId
        ? html`<p class="text-sm text-[color:var(--md-sys-color-on-surface-variant)]">Firma no disponible localmente.</p>`
        : null}
    ${opts.fotoUrl
      ? html`
          <div>
            <p class="mb-2 text-sm font-medium">Registro fotográfico</p>
            <img src=${opts.fotoUrl} alt="Foto" class="max-h-64 w-full rounded-md border object-contain" />
          </div>
        `
      : opts.fotoMediaId
        ? html`<p class="text-sm text-[color:var(--md-sys-color-on-surface-variant)]">Foto no disponible localmente.</p>`
        : null}
  `;
}

/** Footer actions at end of modal form/detail content. */
export function shellModalActions(opts: {
  onCancel: () => void;
  cancelLabel?: string;
  actions: TemplateResult;
}): TemplateResult {
  return html`
    <div class="shell-modal__actions flex flex-col gap-2 pt-2 sm:flex-row">
      <button
        type="button"
        class="md-btn-outlined md-sys-ripple touch-manipulation w-full sm:flex-1"
        @click=${opts.onCancel}
      >
        ${opts.cancelLabel ?? 'Cancelar'}
      </button>
      ${opts.actions}
    </div>
  `;
}

export function shellRecordDetailModal(p: {
  title: string;
  rows: ReadonlyArray<{ label: string; value: TemplateResult | string }>;
  extra?: TemplateResult;
  extraActions?: TemplateResult;
  onClose: () => void;
  onEdit: () => void;
}): TemplateResult {
  return html`
    <div class="shell-modal__body space-y-4">
      ${shellModalHeader({ title: p.title, onClose: p.onClose })}
      <dl class="grid gap-2 text-sm">
        ${p.rows.map(
          (row) => html`
            <div>
              <dt class="font-medium">${row.label}</dt>
              <dd>${row.value}</dd>
            </div>
          `,
        )}
      </dl>
      ${p.extra ?? null}
      ${shellModalActions({
        cancelLabel: 'Cerrar',
        onCancel: p.onClose,
        actions: html`
          ${p.extraActions ?? null}
          <button type="button" class="md-btn-filled md-sys-ripple touch-manipulation w-full sm:flex-1" @click=${p.onEdit}>
            Editar
          </button>
        `,
      })}
    </div>
  `;
}
